import type { ContainerProps } from '@builder4/shared/components/Container';
import { Position } from './Position';
import type { ImageProps } from '@builder4/shared/components/Image';
import type { StylingProps } from '@builder4/shared/types/layout';
import {
  getBoundaryDomElement,
  getNodeContentBoxWidthInPx,
  isPotentiallyFillableElement,
  getNodeTopLeftInParentElement,
  isTextBlockNode,
  hasFixedHeightV2,
} from '@/utils/helperFunc';
import { ROOT_NODE, useEditor, useNode, type NodeId } from '@craftjs/core';
import { useAppSelector } from '@/store/hooks';
import { selectScale } from '@/store/zoom/selectors';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import {
  getElementDimensionsWithoutPadding,
  getElementDimensionsWithPadding,
  pxToPercent,
} from '@/utils/numToMeasurement';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';

const NodePositionSettingsV2 = () => {
  const { actions } = useEditor();
  const { getFillableChilds } = useContainerHelper();
  const { getChildrenExcept } = useCraftHelper();
  const {
    actions: { setProp },
    nodeProps,
    node,
    id,
    parent,
    displayName,
  } = useNode((node) => ({
    nodeProps: node.data.props,
    node: node,
    id: node.id,
    parent: node.data.parent,
    displayName: node.data.displayName,
  }));
  const { query } = useEditor();
  const scale = useAppSelector(selectScale);

  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);
  const position = getBPProp(nodeProps, 'position');
  const getCurrentNodeWidthInPercent = () => {
    // Get the parent node
    const parentNode = query.node(parent as string).get();
    if (!parentNode?.dom) {
      return 0;
    }
    // Get the DOM elements
    const parentElement = getBoundaryDomElement(parentNode.dom);
    const parentWidth = getElementDimensionsWithoutPadding(parentElement).width;
    const childWidth = getNodeContentBoxWidthInPx(node);
    return pxToPercent(childWidth, parentWidth);
  };

  const setTopAndLeftBasedOnCurrentPosition = (newPosition: string) => {
    // Guard: parent may be null in test environments or edge cases
    if (!parent) return;
    const parentNode = query.node(parent as string).get();
    if (!parentNode) return;
    const parentFlexDirection = getBPProp(parentNode.data.props, 'flexDirection');

    // Check if height is fixed
    if (
      hasFixedHeightV2(getBPProp(parentNode.data.props, 'height')) ||
      parentFlexDirection === 'row' ||
      parentNode.id === ROOT_NODE
    ) {
      // height fixed → just set current top/left
      const currentPosition = getNodeTopLeftInParentElement(parentNode, node, scale);
      const { width, height } = getElementDimensionsWithPadding(node.dom as HTMLElement);
      if (currentPosition) {
        actions.history.merge().setProp(id, (props: ImageProps) => {
          const positionAnchorsState = getBPProp(props, 'positionAnchorsState');

          const newPositionAnchorsState = {
            ...positionAnchorsState,
            enableTop: true,
            enableLeft: true,
          };
          setBPProp(props, 'positionAnchorsState', newPositionAnchorsState);
          const padding = getBPProp(parentNode.data.props, 'padding');
          const top = newPosition === 'fixed' ? `${padding[0]}px` : currentPosition.top + 'px';
          setBPProp(props, 'top', top);
          const left = currentPosition.left + 'px';
          setBPProp(props, 'left', left);
          const bottom =
            newPosition === 'fixed' ? `${padding[0] + height}px` : currentPosition.bottom + 'px';
          setBPProp(props, 'bottom', bottom);
          const right = currentPosition.right + 'px';
          setBPProp(props, 'right', right);

          if (isPotentiallyFillableElement(node)) {
            setBPProp(props, 'width', `${width}px`);

            //text block node should not have get fixed height
            if (!isTextBlockNode(node)) {
              setBPProp(props, 'height', `${height}px`);
            }
          }
        });
      }
      return;
    }

    if (parentFlexDirection === 'column') {
      // Flex column → don't change height, set top=0, left=0
      actions.history.merge().setProp(id, (props: ImageProps | ContainerProps) => {
        const positionAnchorsState = getBPProp(props, 'positionAnchorsState');
        const newPositionAnchorsState = {
          ...positionAnchorsState,
          enableTop: true,
          enableLeft: true,
        };
        const padding = getBPProp(parentNode.data.props, 'padding');
        setBPProp(props, 'positionAnchorsState', newPositionAnchorsState);
        const top = padding[0] + 'px';
        setBPProp(props, 'top', top);
        const left = padding[3] + 'px';
        setBPProp(props, 'left', left);
      });
    }
  };

  const adjustParentHeightOnPositionChange = () => {
    const currentNode = query.node(id).get();
    const parentNodeId = currentNode?.data.parent;

    if (!parentNodeId) return;

    const parentNode = query.node(parentNodeId).get();
    if (!parentNode?.dom) return;

    if (!hasFixedHeightV2(getBPProp(parentNode.data.props, 'height'))) {
      // Height is not fixed → check flex direction
      const children = getChildrenExcept(parentNodeId, id);
      const hasRelativeStickySibling = !!children.find((siblingNode) => {
        const position = getBPProp(siblingNode.data.props, 'position');
        return position === 'relative' || position === 'sticky';
      });
      if (!hasRelativeStickySibling) {
        const parentDOM = getBoundaryDomElement(parentNode.dom as HTMLElement);
        const height = getElementDimensionsWithPadding(parentDOM).height;
        actions.history.merge().setProp(parentNodeId, (parentProps: ContainerProps) => {
          setBPProp(parentProps, 'height', `${height}px`, parentNode.data.displayName);
        });
      }
    }
  };

  const handleChange = (newPosition: StylingProps['position']) => {
    //every prop setting after this should be merged
    setProp((props: ImageProps) => {
      setBPProp(props, 'position', newPosition);
    });

    if (newPosition === 'sticky') {
      setProp((props: ImageProps) => {
        const positionAnchorsState = {
          enableTop: true,
          enableRight: false,
          enableBottom: false,
          enableLeft: false,
          enabled: false,
        };
        setBPProp(props, 'positionAnchorsState', positionAnchorsState);
        setBPProp(props, 'bottom', '0px');
        setBPProp(props, 'right', '0px');
        setBPProp(props, 'left', '0px');
      }, 300);
    }

    //button is not fillable so ignoring it for now
    if (isPotentiallyFillableElement(node)) {
      if (!parent) return;
      const parentNode = query.node(parent as string).get();
      if (!parentNode?.dom) return;

      if (newPosition === 'relative' || newPosition === 'sticky') {
        const flexDirection = getBPProp(parentNode.data.props, 'flexDirection');

        if (flexDirection === 'row') {
          //going back to order
          const nodeWidthInPercent = getCurrentNodeWidthInPercent(); // e.g., 30
          if (!nodeWidthInPercent) {
            console.warn('customWidth is undefined');
            return;
          }

          const parentDomElement = getBoundaryDomElement(parentNode.dom);
          const parentWidthPx = getElementDimensionsWithoutPadding(parentDomElement).width;

          const siblings = getFillableChilds(parent as string);
          siblings.forEach((siblingNode) => {
            const siblingRootDom = siblingNode.dom;
            if (!siblingRootDom) return;

            const currentWidthPx = getElementDimensionsWithPadding(siblingRootDom).width; //root div with margin style means overall width
            const currentWidthPercent = pxToPercent(currentWidthPx, parentWidthPx);
            const newWidthInPercent =
              currentWidthPercent - currentWidthPercent * (nodeWidthInPercent / 100);
            actions.history.merge().setProp(siblingNode.id, (props: ImageProps) => {
              setBPProp(props, 'width', `${newWidthInPercent}%`, siblingNode.data.displayName);
              setBPProp(props, 'flexBasis', `${newWidthInPercent}%`, siblingNode.data.displayName);
            });
          });

          actions.history.merge().setProp(id, (props: ImageProps) => {
            setBPProp(props, 'width', `${nodeWidthInPercent}%`);
            setBPProp(props, 'flexBasis', `${nodeWidthInPercent}%`);
            setBPProp(props, 'fillSpace', 'yes');
          });
        } else {
          //either column or root
          actions.history.merge().setProp(id, (props: ImageProps) => {
            setBPProp(props, 'fillSpace', parent === ROOT_NODE ? 'yes' : 'no');
            setBPProp(props, 'width', '100%');
            setBPProp(props, 'flexBasis', '100%');
          });
        }
      }
    }

    if (position === 'relative' && (newPosition === 'absolute' || newPosition === 'fixed')) {
      actions.history.merge().setProp(id, (props: ImageProps) => {
        // Guard: parent may be null in test environments or edge cases
        if (!parent) {
          setBPProp(props, 'zIndex', 1);
          setBPProp(props, 'fillSpace', 'no');
          return;
        }
        const zIndex = getChildrenExcept(parent as NodeId, id as NodeId).length + 1;
        setBPProp(props, 'zIndex', zIndex);
        setBPProp(props, 'fillSpace', 'no');
      });

      adjustParentHeightOnPositionChange();
      setTopAndLeftBasedOnCurrentPosition(newPosition);
    }
  };

  return (
    <Position
      isRootContainer={parent === ROOT_NODE}
      className=""
      onClick={() => {}}
      onChangeCallback={handleChange}
      selectValue={position}
    />
  );
};

export default NodePositionSettingsV2;
